import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, descargar } from "../lib/api";
import { creditos, fechaHora, fecha as soloFecha, iso } from "../lib/formato";
import { Aviso, Cabecera, Campo, Cargando, Etiqueta, Modal, Panel, TarjetaDato, ThOrden, Vacio } from "../componentes/piezas";
import { useSesion } from "../lib/sesion";
import { useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

/**
 * Las cuotas cobradas.
 *
 * Renovaciones dice a quién hay que cobrar; esta pantalla dice a quién se le ha
 * cobrado ya. Son preguntas distintas y hasta ahora la segunda solo se podía
 * responder abriendo las fichas una a una.
 */

interface Cuota {
  id: string;
  fecha: string;
  importeCentimos: number;
  metodoPago: string;
  concepto: string;
  tipo: "ALTA" | "RENOVACION";
  periodoInicio: string;
  periodoFin: string;
  socio: {
    id: string;
    numeroSocio: number;
    nombre: string;
    apellidos: string;
    tipoSocio: { id: string; nombre: string; color: string } | null;
  };
  usuario: { id: string; nombre: string } | null;
  puesto: { id: string; nombre: string } | null;
  /** Cuando se anulo, y por que. Vacio = en pie. */
  anuladaEn: string | null;
  motivoAnulacion: string | null;
}

interface Respuesta {
  cuotas: Cuota[];
  totalCentimos: number;
  porMetodo: Record<string, number>;
  altas: number;
}

interface Tipo {
  id: string;
  nombre: string;
  descripcion: string | null;
  activo: boolean;
  descuentoPorcentaje: number;
  cuotaAnualCentimos: number | null;
  duracionMeses: number;
  _count?: { socios: number };
}

type Pestana = "pagos" | "tipos";
type Columna = "fecha" | "socio" | "tipo" | "forma" | "puesto" | "importe";

const METODOS = ["EFECTIVO", "CARTERA", "TARJETA", "TRANSFERENCIA"];

export default function Cuotas() {
  const { t, cuenta } = useIdioma();
  const { puede, club } = useSesion();

  /** «12 meses» se dice «un año», que es como lo dice cualquiera. */
  const duracion = (meses: number) => {
    if (meses === 12) return t("cu.unAnio");
    if (meses % 12 === 0) return cuenta(meses / 12, "cu.unAnio", "cu.anios");
    return cuenta(meses, "cu.unMes", "cu.meses");
  };
  const primeroDeMes = new Date();
  primeroDeMes.setDate(1);

  const [pestana, setPestana] = useState<Pestana>("pagos");
  const [desde, setDesde] = useState(iso(primeroDeMes));
  const [hasta, setHasta] = useState(iso(new Date()));
  const [buscar, setBuscar] = useState("");
  const [metodoPago, setMetodoPago] = useState("");
  const [tipo, setTipo] = useState("");
  const [tipoSocioId, setTipoSocioId] = useState("");
  const [puestoId, setPuestoId] = useState("");
  const [orden, setOrden] = useState<Columna>("fecha");
  const [descendente, setDescendente] = useState(true);
  const [error, setError] = useState("");
  const [anulando, setAnulando] = useState<Cuota | null>(null);
  const clienteConsultas = useQueryClient();
  const puedeAnular = puede("anularCuota");

  const datos = useQuery({
    queryKey: ["cuotas", desde, hasta, buscar, metodoPago, tipo, tipoSocioId, puestoId],
    queryFn: () =>
      api<Respuesta>("/cuotas", {
        query: {
          desde,
          hasta,
          buscar: buscar.trim() || undefined,
          metodoPago: metodoPago || undefined,
          tipo: tipo || undefined,
          tipoSocioId: tipoSocioId || undefined,
          puestoId: puestoId || undefined,
        },
      }),
    enabled: pestana === "pagos",
  });

  const tipos = useQuery({ queryKey: ["tipos-socio"], queryFn: () => api<Tipo[]>("/tipos-socio") });
  const puestos = useQuery({
    queryKey: ["puestos"],
    queryFn: () => api<{ id: string; nombre: string }[]>("/club/puestos"),
    enabled: puede("ajustesClub"),
  });
  const hayPuestos = (puestos.data?.length ?? 0) > 0;

  const ordenarPor = (c: Columna) => {
    if (c === orden) setDescendente((d) => !d);
    else {
      setOrden(c);
      setDescendente(c === "importe" || c === "fecha");
    }
  };

  const filas = useMemo(() => {
    const lista = [...(datos.data?.cuotas ?? [])];
    const valor = (c: Cuota): string | number => {
      switch (orden) {
        case "socio":
          return `${c.socio.nombre} ${c.socio.apellidos}`.toLowerCase();
        case "tipo":
          return c.socio.tipoSocio?.nombre.toLowerCase() ?? "";
        case "forma":
          return c.metodoPago;
        case "puesto":
          return c.puesto?.nombre.toLowerCase() ?? "";
        case "importe":
          return c.importeCentimos;
        default:
          return c.fecha;
      }
    };
    lista.sort((a, b) => {
      const x = valor(a);
      const y = valor(b);
      const cmp =
        typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y), "es");
      return descendente ? -cmp : cmp;
    });
    return lista;
  }, [datos.data, orden, descendente]);

  return (
    <>
      <Cabecera titulo={t("cu.titulo")} descripcion={t("cu.descripcion")}>
        {pestana === "pagos" && (
          <>
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} style={{ width: 160 }} />
            <span className="suave">{t("cu.a")}</span>
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} style={{ width: 160 }} />
            {puede("verInformes") && (
              <button
                className="boton secundario"
                onClick={() =>
                  descargar(
                    `/exportar/cuotas.csv?desde=${desde}&hasta=${hasta}`,
                    `cuotas-${desde}-a-${hasta}.csv`,
                  ).catch((fallo) => setError(fallo instanceof Error ? fallo.message : t("cu.falloExportar")))
                }
              >
                {t("cu.exportar")}
              </button>
            )}
          </>
        )}
      </Cabecera>

      <Aviso tipo="error">{error}</Aviso>

      <div className="pestanas">
        {(
          [
            ["pagos", t("cu.tabCobradas")],
            ["tipos", t("cu.tabTipos")],
          ] as [Pestana, string][]
        ).map(([clave, texto]) => (
          <button
            key={clave}
            className={`pestana${pestana === clave ? " activa" : ""}`}
            onClick={() => setPestana(clave)}
          >
            {texto}
          </button>
        ))}
      </div>

      {pestana === "pagos" ? (
        <>
          <div className="rejilla tres" style={{ marginBottom: 16 }}>
            <TarjetaDato
              etiqueta={t("cu.cobradoEnPeriodo")}
              valor={creditos(datos.data?.totalCentimos ?? 0)}
              pie={cuenta(filas.length, "cu.unaCuota", "cu.cuotas")}
            />
            <TarjetaDato
              etiqueta={t("cu.altas")}
              valor={String(datos.data?.altas ?? 0)}
              pie={t("cu.altasPie")}
            />
            <TarjetaDato
              etiqueta={t("cu.enEfectivo")}
              valor={creditos(datos.data?.porMetodo.EFECTIVO ?? 0)}
              pie={t("cu.enEfectivoPie")}
            />
          </div>

          <Panel sinCuerpo>
            <div className="filtros-gastos">
              <input
                value={buscar}
                onChange={(e) => setBuscar(e.target.value)}
                placeholder={t("cu.buscar")}
              />
              <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
                <option value="">{t("cu.altasYRenovaciones")}</option>
                <option value="ALTA">{t("cu.soloAltas")}</option>
                <option value="RENOVACION">{t("cu.soloRenovaciones")}</option>
              </select>
              <select value={tipoSocioId} onChange={(e) => setTipoSocioId(e.target.value)}>
                <option value="">{t("cu.cualquierTipo")}</option>
                {(tipos.data ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.nombre}
                  </option>
                ))}
              </select>
              <select value={metodoPago} onChange={(e) => setMetodoPago(e.target.value)}>
                <option value="">{t("cu.cualquierForma")}</option>
                {METODOS.map((m) => (
                  <option key={m} value={m}>
                    {t(`pago.${m}` as Clave)}
                  </option>
                ))}
              </select>
              {hayPuestos && (
                <select value={puestoId} onChange={(e) => setPuestoId(e.target.value)}>
                  <option value="">{t("cu.cualquierPuesto")}</option>
                  {(puestos.data ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {datos.isLoading ? (
              <Cargando />
            ) : filas.length === 0 ? (
              <Vacio>
                {t("cu.sinCoincidencias")} <Link to="/renovaciones">{t("cu.miraRenovaciones")}</Link>.
              </Vacio>
            ) : (
              <div className="tabla-envoltorio">
                <table className="tabla">
                  <thead>
                    <tr>
                      <ThOrden columna="fecha" orden={orden} descendente={descendente} alOrdenar={ordenarPor}>
                        {t("cu.fecha")}
                      </ThOrden>
                      <ThOrden columna="socio" orden={orden} descendente={descendente} alOrdenar={ordenarPor}>
                        {t("cu.socio")}
                      </ThOrden>
                      <ThOrden columna="tipo" orden={orden} descendente={descendente} alOrdenar={ordenarPor}>
                        {t("cu.tipoDeSocio")}
                      </ThOrden>
                      <th>{t("cu.altaORenovacion")}</th>
                      <th>{t("cu.periodo")}</th>
                      <ThOrden columna="forma" orden={orden} descendente={descendente} alOrdenar={ordenarPor}>
                        {t("cu.forma")}
                      </ThOrden>
                      {hayPuestos && (
                        <ThOrden columna="puesto" orden={orden} descendente={descendente} alOrdenar={ordenarPor}>
                          {t("cu.puesto")}
                        </ThOrden>
                      )}
                      <th>{t("cu.cobro")}</th>
                      {puedeAnular && <th />}
                      <ThOrden
                        columna="importe"
                        orden={orden}
                        descendente={descendente}
                        alOrdenar={ordenarPor}
                        num
                      >
                        {t("cu.importe")}
                      </ThOrden>
                    </tr>
                  </thead>
                  <tbody>
                    {filas.map((c) => (
                      <tr key={c.id}>
                        <td className="pequeno suave">{fechaHora(c.fecha)}</td>
                        <td>
                          <Link to={`/socios/${c.socio.id}`}>
                            #{c.socio.numeroSocio} {c.socio.nombre} {c.socio.apellidos}
                          </Link>
                        </td>
                        <td className="pequeno">
                          {c.socio.tipoSocio ? (
                            <span
                              className="etiqueta"
                              style={{
                                background: `${c.socio.tipoSocio.color}20`,
                                color: c.socio.tipoSocio.color,
                              }}
                            >
                              {c.socio.tipoSocio.nombre}
                            </span>
                          ) : (
                            <span className="suave">—</span>
                          )}
                        </td>
                        <td className="pequeno">
                          {c.tipo === "ALTA" ? (
                            <Etiqueta>{t("cu.alta")}</Etiqueta>
                          ) : (
                            <span className="suave">{t("cu.renovacion")}</span>
                          )}
                        </td>
                        <td className="pequeno suave">
                          {soloFecha(c.periodoInicio)} → {soloFecha(c.periodoFin)}
                        </td>
                        <td className="pequeno">{t(`pago.${c.metodoPago}` as Clave)}</td>
                        {hayPuestos && <td className="pequeno">{c.puesto?.nombre ?? "—"}</td>}
                        <td className="pequeno suave">{c.usuario?.nombre ?? "—"}</td>
                        <td className="num tabular">
                          {creditos(c.importeCentimos)}
                          {c.anuladaEn && (
                            <div>
                              <Etiqueta tipo="roja">{t("cu.an.anulada")}</Etiqueta>
                            </div>
                          )}
                        </td>
                        {puedeAnular && (
                          <td style={{ textAlign: "right" }}>
                            {!c.anuladaEn && (
                              <button className="boton fantasma" onClick={() => setAnulando(c)}>
                                {t("cu.an.boton")}
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </>
      ) : (
        <Panel titulo={t("cu.tiposTitulo")}>
          <p className="suave" style={{ marginTop: 0 }}>
            {t("cu.tiposNota")}{" "}
            {puede("ajustesClub") && <Link to="/ajustes">{t("cu.seEditanEnAjustes")}</Link>}
          </p>

          {tipos.isLoading ? (
            <Cargando />
          ) : (tipos.data ?? []).length === 0 ? (
            <Vacio>
              {club?.cuotaAnualCentimos != null
                ? t("cu.sinTiposConCuota", { importe: creditos(club.cuotaAnualCentimos) })
                : t("cu.sinTipos")}
            </Vacio>
          ) : (
            <div className="tabla-envoltorio">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>{t("cu.tipo")}</th>
                    <th className="num">{t("cu.cuota")}</th>
                    <th>{t("cu.dura")}</th>
                    <th className="num">{t("cu.descuento")}</th>
                    <th className="num">{t("cu.socios")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(tipos.data ?? []).map((tipo) => (
                    <tr key={tipo.id} className={tipo.activo ? undefined : "anulada"}>
                      <td>
                        <strong>{tipo.nombre}</strong>
                        {!tipo.activo && <span className="suave pequeno">{t("cu.apagado")}</span>}
                        {tipo.descripcion && <div className="suave pequeno">{tipo.descripcion}</div>}
                      </td>
                      <td className="num tabular">
                        {tipo.cuotaAnualCentimos == null ? (
                          <span className="suave">{t("cu.laDelClub")}</span>
                        ) : (
                          creditos(tipo.cuotaAnualCentimos)
                        )}
                      </td>
                      <td className="pequeno">{duracion(tipo.duracionMeses)}</td>
                      <td className="num tabular">
                        {tipo.descuentoPorcentaje > 0 ? `−${tipo.descuentoPorcentaje}%` : "—"}
                      </td>
                      <td className="num tabular">{tipo._count?.socios ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      {anulando && (
        <DialogoAnularCuota
          cuota={anulando}
          alCerrar={() => setAnulando(null)}
          alFallar={setError}
          alHecho={() => {
            setAnulando(null);
            void clienteConsultas.invalidateQueries({ queryKey: ["cuotas"] });
          }}
        />
      )}
    </>
  );
}

/**
 * Anular una cuota cobrada por error.
 *
 * Se dice lo que va a pasar antes de preguntar, porque son cuatro cosas y
 * ninguna es evidente: al socio le cambia la fecha hasta la que esta al dia, y
 * si era su unica cuota se queda sin poder retirar hasta que pague otra. Un
 * boton que solo dijera «¿seguro?» no avisaria de eso.
 *
 * El motivo es obligatorio y lo pide el servidor: una anulacion sin motivo, en
 * una lista de treinta, no se puede explicar seis meses despues.
 */
function DialogoAnularCuota({
  cuota,
  alCerrar,
  alHecho,
  alFallar,
}: {
  cuota: Cuota;
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (mensaje: string) => void;
}) {
  const { t } = useIdioma();
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);

  return (
    <Modal
      titulo={t("cu.an.titulo", { importe: creditos(cuota.importeCentimos) })}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton fantasma" onClick={alCerrar} disabled={enviando}>
            {t("general.cancelar")}
          </button>
          <button
            className="boton"
            disabled={enviando || motivo.trim().length < 3}
            onClick={async () => {
              setEnviando(true);
              try {
                await api(`/cuotas/${cuota.id}/anular`, {
                  metodo: "POST",
                  cuerpo: { motivo: motivo.trim() },
                });
                alHecho();
              } catch (fallo) {
                setEnviando(false);
                alFallar(fallo instanceof Error ? fallo.message : t("cu.an.fallo"));
                alCerrar();
              }
            }}
          >
            {enviando ? t("cu.an.anulando") : t("cu.an.confirmar")}
          </button>
        </>
      }
    >
      <p>
        {t("cu.an.queVaAPasar", {
          socio: `#${cuota.socio.numeroSocio} ${cuota.socio.nombre} ${cuota.socio.apellidos}`,
          importe: creditos(cuota.importeCentimos),
        })}
      </p>
      <p className="suave pequeno">
        {cuota.metodoPago === "CARTERA" ? t("cu.an.deLaCartera") : t("cu.an.delCajon")}
      </p>
      <Campo etiqueta={t("cu.an.motivo")} ayuda={t("cu.an.motivoAyuda")}>
        <textarea
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          rows={2}
          maxLength={500}
          autoFocus
        />
      </Campo>
    </Modal>
  );
}
